import { checkValidEmails } from "@/Common/helpers/Verifications/emails";
import {
	IInputEmailDistribution,
	IInputSalesLogsEmailDistribution,
	IInputCrossStoreEmailDistribution,
} from "@/Settings/types/SystemSettings/emailDistribution";
import compact from "lodash-es/compact";
import Joi from "joi";
import { ICITAccountNumbers, ICrossStoreAccountDetails } from "@/Settings/types/Common/model";

const six = 6;

const salesLogsEmailDistributionSchema = Joi.object({
	franchises: Joi.array().items(Joi.string().min(1)),
	exportFormat: Joi.string().valid("csv", "pdf", "xlsx"),
	dailyDistributions: Joi.number().min(1).max(six),
	toPositions: Joi.array().items(Joi.string().min(1)),
	toIndividuals: Joi.array().items(
		Joi.object({
			employeeId: Joi.string().min(1),
			_id: Joi.string().min(1),
		})
	),
	toEmails: Joi.array().items(Joi.string().email({ tlds: { allow: false } })),
});

const arrayOfDealerCodesSchema = Joi.array().items(salesLogsEmailDistributionSchema);

const hasNonEmptyString = (value: string) => typeof value === "string" && value.trim().length > 0;

const hasLabelValueObject = (value: unknown) => {
	if (!value || typeof value !== "object") return false;
	const obj = value as Record<string, string>;
	return hasNonEmptyString(obj.label) && hasNonEmptyString(obj.value);
};

export const validateSalesLogsEmailDistribution = (data: IInputSalesLogsEmailDistribution[]) => {
	const { error } = arrayOfDealerCodesSchema.validate(data, {
		abortEarly: false,
	});
	return {
		isValid: !error,
		error,
	};
};

export const validateCitEmailDistribution = (data: IInputEmailDistribution[]) => {
	const errors = data?.map((element) => {
		const hasIndividuals = !!element.toIndividuals?.length;
		const hasNonEmployees = !!compact(element.toNonEmployee)?.length;
		const hasValidNonEmployeeEmails = checkValidEmails(element.toNonEmployee);
		const noIndividualsAndNoNonEmployees = !hasIndividuals && !hasNonEmployees;
		const noIndividualsAndInvalidNonEmployeeEmails = !hasIndividuals && hasNonEmployees && !hasValidNonEmployeeEmails;
		const individualsAndInvalidNonEmployeeEmails = hasIndividuals && hasNonEmployees && !hasValidNonEmployeeEmails;

		return {
			toIndividuals: noIndividualsAndNoNonEmployees,
			toNonEmployee:
				noIndividualsAndNoNonEmployees || noIndividualsAndInvalidNonEmployeeEmails || individualsAndInvalidNonEmployeeEmails,
			accountNumbers: validateAccountNumbers(element.accountNumbers),
		};
	});
	const isValid = errors.every(
		(element) =>
			!element.toIndividuals &&
			!element.toNonEmployee &&
			element.accountNumbers.every((item) => !item.sourceAccount && !item.destinationAccount && !item.sourceLabelId)
	);

	return {
		isValid,
		errors,
	};
};

const validateAccountNumbers = (data: ICITAccountNumbers[]) => {
	const seenCombinations = new Set();
	const results: { sourceAccount: boolean; destinationAccount: boolean; sourceLabelId: boolean }[] = [];

	data.forEach((item) => {
		const result = {
			sourceAccount: false,
			destinationAccount: false,
			sourceLabelId: false,
		};

		// Check required fields
		if (!item.sourceAccount || item.sourceAccount.length === 0) {
			result.sourceAccount = true;
		}

		if (!item.destinationAccount) {
			result.destinationAccount = true;
		}

		if (!item.sourceLabelId) {
			result.sourceLabelId = true;
		}

		// If no fields are invalid, check for duplicates
		if (!result.sourceAccount && !result.destinationAccount && !result.sourceLabelId) {
			item.sourceAccount.forEach((sourceAcc) => {
				const combinationKey = `${sourceAcc}:${item.destinationAccount}`;

				if (seenCombinations.has(combinationKey)) {
					result.sourceAccount = true; // Mark as error for duplicate
				} else {
					seenCombinations.add(combinationKey);
				}
			});
		}

		results.push(result);
	});

	return results;
};

export const validateCrossStoreEmailDistribution = (data: IInputCrossStoreEmailDistribution[]) => {
	const errors = data?.map((element) => {
		const hasIndividuals = !!element.toIndividuals?.length;
		const hasNonEmployees = !!compact(element.toNonEmployee)?.length;
		const hasValidNonEmployeeEmails = checkValidEmails(element.toNonEmployee);
		const noIndividualsAndNoNonEmployees = !hasIndividuals && !hasNonEmployees;
		const noIndividualsAndInvalidNonEmployeeEmails = !hasIndividuals && hasNonEmployees && !hasValidNonEmployeeEmails;
		const individualsAndInvalidNonEmployeeEmails = hasIndividuals && hasNonEmployees && !hasValidNonEmployeeEmails;

		return {
			toIndividuals: noIndividualsAndNoNonEmployees,
			toNonEmployee:
				noIndividualsAndNoNonEmployees || noIndividualsAndInvalidNonEmployeeEmails || individualsAndInvalidNonEmployeeEmails,
			accountMappings: validateAccountMappings(element.accountMappings),
		};
	});
	const isValid = errors.every(
		(element) =>
			!element.toIndividuals &&
			!element.toNonEmployee &&
			element.accountMappings.every(
				(item) =>
					!item.sourceGlAccount &&
					!item.destinationStore &&
					!item.destinationGlAccount &&
					!item.destinationStoreLabel &&
					!item.destinationControlType
			)
	);

	return {
		isValid,
		errors,
	};
};

export const validateAccountMappings = (data: ICrossStoreAccountDetails[]) => {
	const seenCombinations = new Set();
	const results: {
		sourceGlAccount: boolean;
		destinationStore: boolean;
		destinationGlAccount: boolean;
		destinationStoreLabel: boolean;
		destinationControlType: boolean;
	}[] = [];

	data.forEach((item) => {
		const result = {
			sourceGlAccount: false,
			destinationStore: false,
			destinationGlAccount: false,
			destinationStoreLabel: false,
			destinationControlType: false,
		};

		// Check required fields
		if (!item.sourceGlAccount) {
			result.sourceGlAccount = true;
		}

		if (
			!item.destinationStore ||
			typeof item.destinationStore !== "object" ||
			!hasNonEmptyString(item.destinationStore.storeId) ||
			!hasNonEmptyString(item.destinationStore.storeName)
		) {
			result.destinationStore = true;
		}

		if (!item.destinationGlAccount) {
			result.destinationGlAccount = true;
		}

		if (!hasLabelValueObject(item.destinationSourceLabel)) {
			result.destinationStoreLabel = true;
		}

		if (!hasLabelValueObject(item.destinationControlType)) {
			result.destinationControlType = true;
		}

		// If no fields are invalid, check for duplicates
		if (
			!result.sourceGlAccount &&
			!result.destinationStore &&
			!result.destinationGlAccount &&
			!result.destinationStoreLabel &&
			!result.destinationControlType
		) {
			const combinationKey = `${item.sourceGlAccount}:${item.destinationStore}:${item.destinationGlAccount}`;

			if (seenCombinations.has(combinationKey)) {
				result.sourceGlAccount = true; // Mark as error for duplicate
			} else {
				seenCombinations.add(combinationKey);
			}
		}

		results.push(result);
	});

	return results;
};
